import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import type { DashboardQuery, DashboardResponse, ReviewRow } from '../api/quality.js';
import { listReviews } from '../api/quality.js';
import { ApiError } from '../api/client.js';

const STATUSES = ['PENDING_REVIEW', 'IN_REVIEW', 'APPROVED', 'REJECTED', 'REWORK_REQUESTED', 'ESCALATED', 'FORWARDED_TO_BO'] as const;

export function VendorDashboardPage(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly ReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<DashboardQuery>({ status: '', vendorId: '', painterUserId: '', reviewerUserId: '', dateFrom: '', dateTo: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: DashboardResponse = await listReviews(filters, token);
      setRows(res.rows);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  useEffect(() => { void load(); }, [load]);

  return (
    <main aria-label="vendor-dashboard">
      <h1>Vendor Review Dashboard</h1>
      <form aria-label="filters" onSubmit={(e) => { e.preventDefault(); void load(); }}>
        <label>Status
          <select aria-label="filter-status" value={filters.status ?? ''} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Vendor ID
          <input aria-label="filter-vendor" value={filters.vendorId ?? ''} onChange={(e) => setFilters((f) => ({ ...f, vendorId: e.target.value }))} />
        </label>
        <label>Painter ID
          <input aria-label="filter-painter" value={filters.painterUserId ?? ''} onChange={(e) => setFilters((f) => ({ ...f, painterUserId: e.target.value }))} />
        </label>
        <label>Reviewer ID
          <input aria-label="filter-reviewer" value={filters.reviewerUserId ?? ''} onChange={(e) => setFilters((f) => ({ ...f, reviewerUserId: e.target.value }))} />
        </label>
        <label>From
          <input type="date" aria-label="filter-from" value={filters.dateFrom ?? ''} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
        </label>
        <label>To
          <input type="date" aria-label="filter-to" value={filters.dateTo ?? ''} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))} />
        </label>
        <button type="submit">Apply</button>
      </form>
      {loading ? <div role="status">Loading…</div> : null}
      {error ? <div role="alert">{error}</div> : null}
      <div aria-label="total">{total} review(s)</div>
      <table aria-label="reviews-table">
        <thead><tr><th>Review</th><th>Status</th><th>Vendor</th><th>Painter</th><th>Reviewer</th><th>Action</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.reviewId} aria-label={`review-row-${r.reviewId}`}>
              <td>{r.reviewId}</td>
              <td>{r.status}</td>
              <td>{r.vendorId ?? '—'}</td>
              <td>{r.painterUserId ?? '—'}</td>
              <td>{r.assignedReviewerUserId ?? '—'}</td>
              <td><Link to={`/vendor/reviews/${r.reviewId}`} aria-label={`open-${r.reviewId}`}>Open</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
